import type { DailyUptime } from "../api/status";

interface Props {
  /** Days from oldest → newest. */
  days: DailyUptime[];
  /** Total span we want to draw (defaults 60). Days missing from
   *  `days` are rendered as `unknown` blocks so the bar always has the
   *  same width regardless of how much data we have. */
  spanDays?: number;
}

function classify(d: DailyUptime | undefined): {
  className: string;
  tooltip: string;
} {
  if (!d) {
    return {
      className: "bg-status-unknown/40",
      tooltip: "No data",
    };
  }
  let className = "bg-status-ok";
  if (d.uptimePct < 99.9) className = "bg-status-degraded";
  if (d.uptimePct < 99) className = "bg-status-partial";
  if (d.uptimePct < 95) className = "bg-status-major";
  return {
    className,
    tooltip: `${d.date} · ${d.uptimePct.toFixed(2)}% (${d.okProbes}/${d.totalProbes})`,
  };
}

export function UptimeBar({ days, spanDays = 90 }: Props): JSX.Element {
  // Normalise into a span of N slots ordered oldest → newest.
  const byDate = new Map(days.map((d) => [d.date, d]));
  const slots: { day: DailyUptime | undefined; date: string }[] = [];
  const now = new Date();
  // Snap to UTC midnight for stable date keys.
  now.setUTCHours(0, 0, 0, 0);
  for (let i = spanDays - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setUTCDate(d.getUTCDate() - i);
    const key = d.toISOString().slice(0, 10);
    slots.push({ day: byDate.get(key), date: key });
  }

  return (
    <div
      className="flex w-full items-stretch gap-[2px]"
      role="img"
      aria-label={`${spanDays}-day uptime`}
    >
      {slots.map((s) => {
        const { className, tooltip } = classify(s.day);
        return (
          <div
            key={s.date}
            title={tooltip}
            className={`h-7 flex-1 min-w-[2px] rounded-[2px] transition-opacity ${className} hover:opacity-70`}
          />
        );
      })}
    </div>
  );
}
